/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { ApiResponse } from "../../interfaces/Api";
import { Training } from "../../interfaces/TrainingInterface";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import {
  getTrainings,
  getTrainingById,
  postTraining,
  patchTraining,
  deleteTraining,
  GetTrainingsParams
} from "../../api/training";

export const trainingsQueryKey = "trainings";

export type UseTrainingsOptions = Omit<GetTrainingsParams, "resourceCentreId">;

// use trainings
export const useTrainings = (
  options?: number | UseTrainingsOptions
): UseQueryResult<Array<Training>, Error> => {
  const resourceCentre = useCurrentResourceCentre();
  const parsedOptions: UseTrainingsOptions =
    typeof options === "number" ? { clientId: options } : options || {};

  return useQuery({
    queryKey: [
      trainingsQueryKey,
      resourceCentre?.id,
      parsedOptions.clientId,
      parsedOptions.search,
      parsedOptions.certificateNumber,
      parsedOptions.page,
      parsedOptions.limit
    ],
    queryFn: async () => {
      if (!resourceCentre?.id) return [];

      const response = await getTrainings({
        resourceCentreId: Number(resourceCentre.id),
        ...parsedOptions
      });

      return response.data || [];
    },
    enabled: !!resourceCentre?.id
  });
};

// alias for singular naming preference
export const useTraining = useTrainings;

// use training by id
export const useTrainingById = (id?: number | string): UseQueryResult<Training | null, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [trainingsQueryKey, id, resourceCentre?.id],
    queryFn: async () => {
      if (!id) return null;

      const response = await getTrainingById(id);
      return response.data || null;
    },
    enabled: !!id && !!resourceCentre?.id
  });
};

// use post training
export const usePostTraining = (): UseMutationResult<
  ApiResponse<Training>,
  Error,
  Partial<Training>,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: (data: Partial<Training>) =>
      postTraining({
        ...data,
        resourceCentreId: data.resourceCentreId || Number(resourceCentre?.id)
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [trainingsQueryKey, resourceCentre?.id] });
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: response?.message || "Training created successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.response?.data?.message || error?.message || "Failed to create training",
          variant: "error"
        })
      );
    }
  });
};

// use patch training
export const usePatchTraining = (): UseMutationResult<
  ApiResponse<Training>,
  Error,
  { id: number | string; data: Partial<Training> },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: ({ id, data }) => patchTraining({ id, data }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [trainingsQueryKey, resourceCentre?.id] });
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: response?.message || "Training updated successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.response?.data?.message || error?.message || "Failed to update training",
          variant: "error"
        })
      );
    }
  });
};

// use delete training
export const useDeleteTraining = (): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: (id) => deleteTraining(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [trainingsQueryKey, resourceCentre?.id] });
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: response?.message || "Training deleted successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.response?.data?.message || error?.message || "Failed to delete training",
          variant: "error"
        })
      );
    }
  });
};

export default useTraining;
